/**
 * Vehicle transfers between mitrs (owner, 13 Oct 2026): what the screens read
 * from /transfers, and the challan download every one of them offers.
 *
 * A counter's lists name their book outright — its own line (`book: null`)
 * or the TVS line through its linked party (`book: 'tvs'`) — the way the
 * counter home reads its two stocks. Everything polls once a minute: the app
 * has no phone push, so the lists are the notification.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { HEAD_ADMIN_DESK_EMAILS, challanFileName, type TransferListState, type TransferParty, type VehicleTransferRow } from '@bright/shared';

import { ApiError, api, downloadFile, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useActiveBook, type BookKey } from '@/lib/book';
import { useToast } from '@/components/ui';

/** Which of a counter's two lines a list reads. */
export type CounterBook = 'own' | 'tvs';

const bookOption = (book: CounterBook) => ({ book: book === 'tvs' ? ('tvs' as const) : null });

export const transferKeys = {
  all: ['transfers'] as const,
  incoming: (book: CounterBook) => ['transfers', 'incoming', book] as const,
  sent: (book: CounterBook, state: 'live' | 'recent') => ['transfers', 'sent', book, state] as const,
  receivers: (book: CounterBook) => ['transfers', 'receivers', book] as const,
  approvals: (state: TransferListState) => ['transfers', 'approvals', state] as const,
  book: (state: TransferListState, active: BookKey | null) => ['transfers', 'book', state, active] as const,
  vehicle: (vehicleId: string, active: BookKey | null) => ['transfers', 'vehicle', vehicleId, active] as const,
};

/**
 * A minute's poll, stopped only when the line REFUSES (a Bright on Wheels
 * counter with no TVS twin is refused there, and asking again every minute
 * would only be refused every minute). A passing failure — the minute the API
 * restarts on a deploy, a dropped connection — keeps the poll going: stopped
 * on any error, a phone left on the home all day never saw the next approval
 * (review, 13 Oct 2026).
 */
const refused = (error: unknown) => error instanceof ApiError && [401, 403, 404].includes(error.status);
const everyMinuteUntilRefused = (error: unknown) => (refused(error) ? false : 60_000);

/**
 * The vehicle detail query's key — `vehicleKeys.detail` in components/StockTable,
 * written out here so the counter home does not pull the stock table in.
 */
const vehicleDetailKey = (id: string) => ['vehicles', 'detail', id] as const;

/** Approved transfers waiting for this counter's tick. */
export function useIncomingTransfers(book: CounterBook, enabled = true) {
  return useQuery({
    queryKey: transferKeys.incoming(book),
    queryFn: () => api.get<{ data: VehicleTransferRow[] }>('/transfers/incoming', undefined, bookOption(book)),
    enabled,
    retry: book === 'tvs' ? false : 3,
    refetchInterval: (query) => everyMinuteUntilRefused(query.state.error),
    refetchOnWindowFocus: (query) => !refused(query.state.error),
  });
}

/** What this counter has sent: the live ones, or the last thirty days. */
export function useSentTransfers(book: CounterBook, state: 'live' | 'recent', enabled = true) {
  return useQuery({
    queryKey: transferKeys.sent(book, state),
    queryFn: () => api.get<{ data: VehicleTransferRow[] }>('/transfers/sent', { state }, bookOption(book)),
    enabled,
    retry: book === 'tvs' ? false : 3,
    refetchInterval: (query) => everyMinuteUntilRefused(query.state.error),
    refetchOnWindowFocus: (query) => !refused(query.state.error),
  });
}

/**
 * The vehicles a counter has in a live transfer — its stock cards say so
 * instead of offering Sell. Read off the same list "Transfers you sent" shows
 * (the server puts every live one in it, whatever its age), so the home asks
 * once a minute, not twice.
 */
export function useTransfersInFlight(book: CounterBook, enabled = true): Set<string> {
  const sent = useSentTransfers(book, 'recent', enabled);
  const queryClient = useQueryClient();
  const live = useMemo(
    () =>
      new Set(
        (sent.data?.data ?? [])
          .filter((row) => (row.status === 'PENDING_APPROVAL' || row.status === 'PENDING_ACCEPTANCE') && !row.stale)
          .map((row) => row.vehicle.id),
      ),
    [sent.data],
  );
  /*
   * A transfer that leaves the live set — accepted, declined, refused or
   * withdrawn on someone else's phone — changes what this counter holds. The
   * stock list does not poll, so without this it went on offering Sell and
   * Transfer on a vehicle that was no longer here (review, 13 Oct 2026).
   */
  const previous = useRef<Set<string> | null>(null);
  useEffect(() => {
    const before = previous.current;
    previous.current = live;
    if (!before) return;
    const gone = [...before].filter((id) => !live.has(id));
    if (gone.length === 0) return;
    void queryClient.invalidateQueries({ queryKey: ['portal-stock'] });
    void queryClient.invalidateQueries({ queryKey: ['my-sales'] });
    for (const id of gone) void queryClient.invalidateQueries({ queryKey: vehicleDetailKey(id) });
  }, [live, queryClient]);
  return live;
}

export function useTransferReceivers(book: CounterBook, enabled: boolean) {
  return useQuery({
    queryKey: transferKeys.receivers(book),
    queryFn: () => api.get<{ data: TransferParty[] }>('/transfers/receivers', undefined, bookOption(book)),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/** Whether this person approves transfers — a switch on the person (Surbhi, and Abhishek in her absence). */
export function useApprovesTransfers(): boolean {
  const { user } = useAuth();
  return Boolean(user?.approvesTransfers && !user.scopeParty);
}

/** The approver's queue, across every book they approve in. */
export function useTransferApprovals(state: TransferListState, enabled: boolean) {
  return useQuery({
    queryKey: transferKeys.approvals(state),
    queryFn: () => api.get<{ data: VehicleTransferRow[]; books: string[] }>('/transfers/approvals', { state }, { book: null }),
    enabled,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}

/** The book's transfers, read only, for the office. */
export function useBookTransfers(state: TransferListState, enabled: boolean) {
  const active = useActiveBook();
  return useQuery({
    queryKey: transferKeys.book(state, active),
    queryFn: () => api.get<{ data: VehicleTransferRow[] }>('/transfers', { state }),
    enabled,
    refetchInterval: 60_000,
  });
}

/** The live transfer on a vehicle, for its page — null when there is none, or none this person may see. */
export function useVehicleTransfer(vehicleId: string, enabled: boolean) {
  const active = useActiveBook();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: transferKeys.vehicle(vehicleId, active),
    queryFn: () => api.get<{ transfer: VehicleTransferRow | null }>(`/transfers/vehicle/${vehicleId}`),
    enabled: enabled && vehicleId.length > 0,
    refetchInterval: 60_000,
  });
  // The transfer ended while the page was open: the vehicle itself may have moved, so read it again.
  const liveId = query.data?.transfer?.id ?? null;
  const previous = useRef<string | null>(null);
  useEffect(() => {
    const before = previous.current;
    previous.current = liveId;
    if (before && before !== liveId) void queryClient.invalidateQueries({ queryKey: vehicleDetailKey(vehicleId) });
  }, [liveId, vehicleId, queryClient]);
  return query;
}

/** Whether this sign-in is a head-admin desk — the only one that flips the approval switch in Users. */
export function isHeadAdminDesk(email: string | null | undefined): boolean {
  return Boolean(email) && HEAD_ADMIN_DESK_EMAILS.includes(email!.trim().toLowerCase());
}

/**
 * Save a transfer's challan to the phone, saying so when it fails — never a
 * bare `void downloadFile()` (SalePapers, 14 Sep 2026). The book is the
 * transfer's own, so a TVS challan is read through the TVS line.
 */
export function useSaveChallan() {
  const toast = useToast();
  const [savingId, setSavingId] = useState<string | null>(null);
  async function save(row: Pick<VehicleTransferRow, 'id' | 'challanNo' | 'bookKey'>): Promise<boolean> {
    setSavingId(row.id);
    try {
      await downloadFile(`/transfers/${row.id}/challan.pdf`, challanFileName(row.challanNo), { book: (row.bookKey as BookKey | null) ?? null });
      return true;
    } catch (cause) {
      toast.error('The challan did not download', cause instanceof ApiError || cause instanceof Error ? errorMessage(cause) : 'Try the download button again.');
      return false;
    } finally {
      setSavingId(null);
    }
  }
  return { save, savingId };
}

/** "13 Oct" in IST. */
export function transferDay(iso: string | null | undefined): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', timeZone: 'Asia/Kolkata' });
}

/** "Waiting for approval", or who answered once someone has — never a name the row does not carry. */
export function transferStage(row: VehicleTransferRow): string {
  if (row.status === 'PENDING_APPROVAL') return 'Waiting for approval';
  if (row.status === 'PENDING_ACCEPTANCE') return `Approved by ${row.approval?.by ?? 'the office'} · waiting for ${row.to.name}`;
  if (row.status === 'COMPLETED') return `Accepted by ${row.receiver?.by ?? row.to.name} with all accessories`;
  if (row.status === 'DECLINED') return `Not approved${row.approval?.by ? ` by ${row.approval.by}` : ''}${row.approval?.note ? ` — ${row.approval.note}` : ''}`;
  if (row.status === 'REFUSED') return `Refused by ${row.receiver?.by ?? row.to.name}${row.receiver?.note ? ` — ${row.receiver.note}` : ''}`;
  return row.cancelled?.note ?? row.statusLabel;
}

/** Where a counter is: "Dewa · Barabanki". */
export function placeOf(party: TransferParty): string {
  return [party.city, party.district].filter(Boolean).join(' · ');
}
